/**
 * Thread rendering logic for ticket detail (artboards 02 and 05), kept pure so
 * it is unit-tested (IMPLEMENTATION_PLAN §12):
 * - views: conversation (open ticket: no internal notes), full, customer-only, notes
 * - day separators in the viewer's time zone
 * - consecutive internal notes grouped into one yellow block
 * - the Resolved block and CSAT placed chronologically among the messages
 */
import type { MessageView } from '@/api/messagesApi'
import type { TicketCsat, TicketResolution } from '@/api/ticketsApi'

export type ThreadView = 'conversation' | 'full' | 'customer' | 'notes'

export type ThreadItem =
  | { kind: 'day'; key: string; label: string }
  | { kind: 'message'; key: string; message: MessageView; firstResponseMins: number | null }
  | { kind: 'system'; key: string; message: MessageView }
  | { kind: 'notes'; key: string; notes: MessageView[] }
  | { kind: 'resolved'; key: string; resolution: TicketResolution }
  | { kind: 'csat'; key: string; csat: TicketCsat }

export interface ThreadTicket {
  createdAt: string
  sla?: { firstRespondedAt: string | null } | null
  resolution?: TicketResolution | null
  csat?: TicketCsat | null
}

const MINUTE = 60_000

/** The browser's IANA zone (falls back to UTC). */
export function viewerTimeZone(): string {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC'
  } catch {
    return 'UTC'
  }
}

function parts(date: Date, timeZone: string, options: Intl.DateTimeFormatOptions): Record<string, string> {
  const out: Record<string, string> = {}
  for (const p of new Intl.DateTimeFormat('en-GB', { timeZone, ...options }).formatToParts(date)) {
    out[p.type] = p.value
  }
  return out
}

/** `2026-10-16` in `timeZone`; the grouping key for day separators. */
export function dayKey(iso: string, timeZone: string): string {
  const p = parts(new Date(iso), timeZone, { year: 'numeric', month: '2-digit', day: '2-digit' })
  return `${p.year}-${p.month}-${p.day}`
}

/** `Friday, 16 October 2026` (artboard 05 day divider). */
export function dayLabel(iso: string, timeZone: string): string {
  const p = parts(new Date(iso), timeZone, { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' })
  return `${p.weekday}, ${p.day} ${p.month} ${p.year}`
}

/** `14:02` (24-hour) in `timeZone`. */
export function timeLabel(iso: string, timeZone: string): string {
  const p = parts(new Date(iso), timeZone, { hour: '2-digit', minute: '2-digit', hourCycle: 'h23' })
  return `${p.hour}:${p.minute}`
}

/** `16 Oct 2026 16:58:12 MST` (Resolved block date & time). */
export function dateTimeLabel(iso: string, timeZone: string): string {
  const p = parts(new Date(iso), timeZone, {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    hourCycle: 'h23',
  })
  // en-US knows the US abbreviations (MST, EDT); en-GB would print GMT-7.
  const zone = new Intl.DateTimeFormat('en-US', { timeZone, timeZoneName: 'short' })
    .formatToParts(new Date(iso))
    .find((x) => x.type === 'timeZoneName')?.value
  return `${p.day} ${p.month} ${p.year} ${p.hour}:${p.minute}:${p.second}${zone ? ` ${zone}` : ''}`
}

/** `7m`, `1h 05m`, `2d 3h`. */
export function formatDuration(ms: number): string {
  const mins = Math.max(0, Math.round(ms / MINUTE))
  if (mins < 60) return `${mins}m`
  const hours = Math.floor(mins / 60)
  if (hours < 24) return `${hours}h ${String(mins % 60).padStart(2, '0')}m`
  return `${Math.floor(hours / 24)}d ${hours % 24}h`
}

/** Which messages a view shows. */
export function filterMessages(messages: MessageView[], view: ThreadView): MessageView[] {
  switch (view) {
    case 'full':
      return messages
    case 'conversation':
      return messages.filter((m) => m.kind !== 'internal_note')
    case 'customer':
      return messages.filter((m) => m.visibility === 'customer' && m.kind !== 'internal_note')
    case 'notes':
      return messages.filter((m) => m.kind === 'internal_note')
  }
}

/** "via portal" / "via email" for customer-authored messages; nothing for desk or API. */
export function channelOf(message: MessageView): 'portal' | 'email' | null {
  if (message.kind !== 'customer_reply') return null
  return message.channel === 'portal' || message.channel === 'email' ? message.channel : null
}

type Timed =
  | { at: number; order: number; item: { kind: 'msg'; message: MessageView } }
  | { at: number; order: number; item: { kind: 'resolved'; resolution: TicketResolution } }
  | { at: number; order: number; item: { kind: 'csat'; csat: TicketCsat } }

/**
 * Build the render list for a view. Messages are assumed chronological (the API
 * sorts them); resolution and CSAT are merged in by time, after any message
 * with the same timestamp.
 */
export function buildThread(
  messages: MessageView[],
  ticket: ThreadTicket,
  view: ThreadView,
  timeZone: string,
): ThreadItem[] {
  const visible = filterMessages(messages, view)
  const timed: Timed[] = visible.map((message, i) => ({
    at: Date.parse(message.createdAt),
    order: i,
    item: { kind: 'msg', message },
  }))
  if (view !== 'notes') {
    if (ticket.resolution) {
      timed.push({ at: Date.parse(ticket.resolution.resolvedAt), order: 1e6, item: { kind: 'resolved', resolution: ticket.resolution } })
    }
    if (ticket.csat) {
      timed.push({ at: Date.parse(ticket.csat.createdAt), order: 1e6 + 1, item: { kind: 'csat', csat: ticket.csat } })
    }
  }
  timed.sort((a, b) => a.at - b.at || a.order - b.order)

  const firstRespondedAt = ticket.sla?.firstRespondedAt ? Date.parse(ticket.sla.firstRespondedAt) : null
  const openedAt = Date.parse(ticket.createdAt)

  const out: ThreadItem[] = []
  let currentDay: string | null = null
  let noteGroup: MessageView[] | null = null

  for (const t of timed) {
    const iso = new Date(t.at).toISOString()
    const day = dayKey(iso, timeZone)
    if (day !== currentDay) {
      currentDay = day
      noteGroup = null
      out.push({ kind: 'day', key: `day-${day}`, label: dayLabel(iso, timeZone) })
    }

    if (t.item.kind === 'resolved') {
      noteGroup = null
      out.push({ kind: 'resolved', key: `resolved-${t.item.resolution.sequence}`, resolution: t.item.resolution })
      continue
    }
    if (t.item.kind === 'csat') {
      noteGroup = null
      out.push({ kind: 'csat', key: `csat-${t.at}`, csat: t.item.csat })
      continue
    }

    const m = t.item.message
    if (m.kind === 'internal_note') {
      if (noteGroup) {
        noteGroup.push(m)
      } else {
        noteGroup = [m]
        out.push({ kind: 'notes', key: `notes-${m.id}`, notes: noteGroup })
      }
      continue
    }
    noteGroup = null
    if (m.kind === 'system_event') {
      out.push({ kind: 'system', key: m.id, message: m })
      continue
    }
    const isFirstResponse =
      m.kind === 'agent_reply' && firstRespondedAt !== null && Math.abs(Date.parse(m.createdAt) - firstRespondedAt) < 1000
    out.push({
      kind: 'message',
      key: m.id,
      message: m,
      firstResponseMins: isFirstResponse ? Math.round((firstRespondedAt - openedAt) / MINUTE) : null,
    })
  }
  return out
}

/** The reply composer's addressee: the most recent customer author, else the reporter. */
export function replyTarget(messages: MessageView[], reporterName: string | null | undefined): string | null {
  for (let i = messages.length - 1; i >= 0; i--) {
    if (messages[i].kind === 'customer_reply') return firstName(messages[i].author.name)
  }
  return reporterName ? firstName(reporterName) : null
}

export function firstName(name: string): string {
  return name.trim().split(/\s+/)[0] ?? name
}

/** `KO` from `Kwame Osei`. */
export function initialsOf(name: string): string {
  const words = name.trim().split(/\s+/).filter(Boolean)
  if (words.length === 0) return '?'
  const first = words[0][0] ?? ''
  const last = words.length > 1 ? (words[words.length - 1][0] ?? '') : ''
  return (first + last).toUpperCase()
}

/** `212 KB`, `1.4 MB` (artboard 05 attachment chips). */
export function formatBytes(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`
  const kb = bytes / 1024
  if (kb < 1024) return `${Math.round(kb)} KB`
  const mb = kb / 1024
  return `${mb < 10 ? mb.toFixed(1) : Math.round(mb)} MB`
}

/** `PNG` from `label-error.png`; falls back to the MIME subtype. */
export function fileBadge(fileName: string, mimeType?: string): string {
  const dot = fileName.lastIndexOf('.')
  if (dot > 0 && dot < fileName.length - 1) return fileName.slice(dot + 1).toUpperCase().slice(0, 4)
  const sub = mimeType?.split('/')[1]
  return (sub ?? 'FILE').toUpperCase().slice(0, 4)
}

/** `14:02` when `iso` is today in `timeZone`, else `16 Oct 14:02` (header "Opened …" line). */
export function whenLabel(iso: string, timeZone: string, now: number = Date.now()): string {
  const time = timeLabel(iso, timeZone)
  if (dayKey(iso, timeZone) === dayKey(new Date(now).toISOString(), timeZone)) return time
  const p = parts(new Date(iso), timeZone, { day: 'numeric', month: 'short' })
  return `${p.day} ${p.month} ${time}`
}
